<template>
  <hs-modal v-model="open" title="Join Group" size="md" @close="$emit('close')">
    <div class="jgm-body">
      <div class="jgm-steps">
        <div v-for="step in 2" :key="step" class="jgm-steps__item">
          <div class="jgm-step-dot" :class="getStepClass(step)">
            <hs-icon v-if="step < currentStep" name="check" :size="12" />
            <span v-else>{{ step }}</span>
          </div>
          <div v-if="step < 2" class="jgm-step-line" :class="{ 'jgm-step-line--done': currentStep > step }" />
        </div>
      </div>

      <div v-if="currentStep === 1" class="jgm-section">
        <p class="jgm-desc">Enter the join code provided by the group administrator.</p>

        <div class="jgm-info-box">
          <span class="jgm-info-box__label">Store being added</span>
          <span class="jgm-info-box__value">Store #{{ storeId }}</span>
        </div>

        <hs-input
          v-model="joinCode"
          label="Join Code"
          placeholder="Enter the join code"
          required
          hint="Typically 8–12 characters, case-insensitive"
          @change="joinCode = joinCode.toUpperCase()"
          @keyup.enter="validateCode"
        />
      </div>

      <div v-if="currentStep === 2 && groupInfo" class="jgm-section">
        <hs-alert tone="success" title="Valid join code!" message="Confirm that you want to join this group:" />

        <div class="jgm-group-card">
          <div class="jgm-group-card__name">{{ groupInfo.name }}</div>
          <p v-if="groupInfo.description" class="jgm-group-card__desc">{{ groupInfo.description }}</p>

          <div v-if="groupInfo.settings" class="jgm-features">
            <span class="jgm-features__label">Group Features</span>
            <div class="jgm-features__list">
              <hs-badge v-if="groupInfo.settings.sync_products" tone="primary" icon="refresh-cw">Product Sync</hs-badge>
              <hs-badge v-if="groupInfo.settings.sync_services" tone="primary" icon="refresh-cw">Service Sync</hs-badge>
              <hs-badge v-if="groupInfo.settings.require_sku" tone="warning" icon="hash">SKU Required</hs-badge>
              <hs-badge v-if="!groupInfo.settings.sync_products && !groupInfo.settings.sync_services" tone="neutral">No features enabled</hs-badge>
            </div>
          </div>
        </div>

        <div class="jgm-info-panel">
          <div class="jgm-info-panel__title">What happens when you join?</div>
          <ul class="jgm-info-panel__list">
            <li>Your store will be added as a MEMBER</li>
            <li v-if="groupInfo.settings?.sync_products">You can share products with other group members</li>
            <li v-if="groupInfo.settings?.sync_services">You can share services with other group members</li>
            <li>Group administrators can manage your role and permissions</li>
          </ul>
        </div>
      </div>

      <hs-alert v-if="error" tone="danger" :message="error" />
    </div>

    <template #footer>
      <div class="jgm-footer">
        <hs-button variant="secondary" type="button" @click="currentStep === 1 ? $emit('close') : goBack()">
          {{ currentStep === 1 ? 'Cancel' : 'Back' }}
        </hs-button>

        <hs-button
          v-if="currentStep === 1"
          variant="primary"
          icon="search"
          :loading="validating"
          :disabled="validating || !joinCode.trim()"
          @click="validateCode"
        >
          Validate Code
        </hs-button>

        <hs-button
          v-if="currentStep === 2"
          variant="primary"
          icon="check"
          :loading="joining"
          :disabled="joining"
          @click="confirmJoin"
        >
          Confirm & Join
        </hs-button>
      </div>
    </template>
  </hs-modal>
</template>

<script>
import { ref } from "vue";
import { toast } from "vue3-toastify";
import http from "@/helper/http";
import { HsModal, HsInput, HsButton, HsAlert, HsBadge, HsIcon } from "@/components/ui";

export default {
  name: "JoinGroupModal",
  components: { HsModal, HsInput, HsButton, HsAlert, HsBadge, HsIcon },
  props: {
    storeId: { type: Number, required: true },
  },
  emits: ["close", "joined"],
  setup(props, { emit }) {
    const open = ref(true);
    const currentStep = ref(1);
    const validating = ref(false);
    const joining = ref(false);
    const error = ref("");
    const joinCode = ref("");
    const groupInfo = ref(null);

    const getStepClass = (step) => {
      if (step < currentStep.value) return "jgm-step-dot--done";
      if (step === currentStep.value) return "jgm-step-dot--active";
      return "";
    };

    const validateCode = async () => {
      const code = joinCode.value.trim();
      if (!code) { error.value = "Please enter a join code."; return; }

      validating.value = true;
      error.value = "";

      try {
        const response = await http.post(`/v1/stores/${props.storeId}/groups/validate-code`, { join_code: code });
        groupInfo.value = response.data.group;
        currentStep.value = 2;
        toast.success("Join code validated successfully!");
      } catch (err) {
        console.error("Failed to validate join code", err);
        const message = err?.response?.data?.error?.message;
        if (message?.includes("already")) error.value = "This store is already a member of this group.";
        else if (message?.includes("code") || message?.includes("Invalid")) error.value = "Invalid or expired join code.";
        else if (message?.includes("expired")) error.value = "This join code has expired. Please request a new one.";
        else if (message?.includes("different store")) error.value = "This join code is for a different store.";
        else error.value = message || "Unable to validate join code. Please try again.";
      } finally {
        validating.value = false;
      }
    };

    const confirmJoin = async () => {
      joining.value = true;
      error.value = "";

      try {
        const response = await http.post(`/v1/stores/${props.storeId}/groups/join`, { join_code: joinCode.value.trim() });
        toast.success(`Successfully joined "${groupInfo.value.name}"!`);
        emit("joined", response.data);
        emit("close");
      } catch (err) {
        console.error("Failed to join group", err);
        error.value = err?.response?.data?.error?.message || "Unable to join group. Please try again.";
      } finally {
        joining.value = false;
      }
    };

    const goBack = () => { currentStep.value = 1; groupInfo.value = null; error.value = ""; };

    return { open, currentStep, validating, joining, error, joinCode, groupInfo, getStepClass, validateCode, confirmJoin, goBack };
  },
};
</script>

<style scoped>
.jgm-body {
  display: grid;
  gap: 1.1rem;
}

.jgm-steps {
  align-items: center;
  display: flex;
  gap: 0;
}

.jgm-steps__item {
  align-items: center;
  display: flex;
}

.jgm-step-dot {
  align-items: center;
  border: 2px solid color-mix(in srgb, var(--hs-border) 80%, transparent);
  border-radius: 50%;
  color: var(--hs-muted);
  display: inline-flex;
  font-size: 0.78rem;
  font-weight: 750;
  height: 2rem;
  justify-content: center;
  width: 2rem;
  transition: all 0.2s;
}

.jgm-step-dot--active {
  background: var(--hs-primary);
  border-color: var(--hs-primary);
  color: #fff;
}

.jgm-step-dot--done {
  background: #10b981;
  border-color: #10b981;
  color: #fff;
}

.jgm-step-line {
  background: color-mix(in srgb, var(--hs-border) 70%, transparent);
  height: 2px;
  width: 3rem;
  transition: background 0.2s;
}

.jgm-step-line--done {
  background: #10b981;
}

.jgm-section {
  display: grid;
  gap: 1rem;
}

.jgm-desc {
  color: var(--hs-muted);
  font-size: 0.83rem;
  margin: 0;
}

.jgm-info-box {
  background: color-mix(in srgb, var(--hs-bg-soft) 72%, transparent);
  border: 1px solid var(--hs-border);
  border-radius: 10px;
  display: grid;
  gap: 0.2rem;
  padding: 0.75rem 1rem;
}

.jgm-info-box__label {
  color: var(--hs-muted);
  font-size: 0.72rem;
  font-weight: 750;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.jgm-info-box__value {
  color: var(--hs-text);
  font-size: 0.88rem;
  font-weight: 600;
}

.jgm-group-card {
  background: color-mix(in srgb, var(--hs-surface) 90%, transparent);
  border: 1px solid var(--hs-border);
  border-radius: 12px;
  display: grid;
  gap: 0.5rem;
  padding: 1rem 1.1rem;
}

.jgm-group-card__name {
  color: var(--hs-text);
  font-size: 1rem;
  font-weight: 750;
}

.jgm-group-card__desc {
  color: var(--hs-muted);
  font-size: 0.82rem;
  margin: 0;
}

.jgm-features {
  display: grid;
  gap: 0.45rem;
  margin-top: 0.35rem;
}

.jgm-features__label {
  color: var(--hs-muted);
  font-size: 0.72rem;
  font-weight: 750;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.jgm-features__list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.jgm-info-panel {
  background: color-mix(in srgb, var(--hs-primary) 6%, transparent);
  border: 1px solid color-mix(in srgb, var(--hs-primary) 20%, transparent);
  border-radius: 10px;
  padding: 0.9rem 1rem;
}

.jgm-info-panel__title {
  color: var(--hs-text);
  font-size: 0.8rem;
  font-weight: 750;
  margin-bottom: 0.45rem;
}

.jgm-info-panel__list {
  color: var(--hs-muted);
  display: grid;
  font-size: 0.78rem;
  gap: 0.3rem;
  list-style: disc;
  padding-left: 1.1rem;
}

.jgm-footer {
  display: flex;
  gap: 0.65rem;
  justify-content: flex-end;
}
</style>
